import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import toast from '../../components/ui/Toast';
import NavigationSidebar from '../../components/NavigationSidebar';
import TutorialAccessButton from '../../components/TutorialAccessButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import agentService from '../../services/agentService';
import TaskDashboard from './components/TaskDashboard';
import DeadlineCalendar from './components/DeadlineCalendar';
import SMSReminderSettings from './components/SMSReminderSettings';
import DailyDigestView from './components/DailyDigestView';
import CreateTaskModal from './components/CreateTaskModal';
import SmartSchedulingPanel from './components/SmartSchedulingPanel';
import PriorityMatrix from './components/PriorityMatrix';
import AgentChatInterface from '../../components/AgentChatInterface';
import ErrorBoundary from '../../components/ErrorBoundary';


const PersonalProductivityAgentInterface = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, currentTeamId, currentOrganizationId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [activeView, setActiveView] = useState('dashboard');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);

  const views = [
    { id: 'chat', label: 'Chat with Agent', icon: 'MessageSquare' },
    { id: 'dashboard', label: 'Task Dashboard', icon: 'LayoutDashboard' },
    { id: 'calendar', label: 'Deadline Calendar', icon: 'Calendar' },
    { id: 'digest', label: 'Daily Digest', icon: 'FileText' },
    { id: 'reminders', label: 'SMS Reminders', icon: 'Bell' }
  ];

  useEffect(() => {
    if (user?.id) {
      loadTasks();
    }
  }, [user]);

  const loadTasks = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await agentService?.getTasks(user?.id, {
        agentId: 'personal-productivity',
        teamId: currentTeamId
      });
      
      if (error) throw error;
      setTasks(data || []);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast?.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (taskData) => {
    try {
      const { data, error } = await agentService?.createTask(
        user?.id,
        'personal-productivity',
        taskData,
        currentOrganizationId
      );
      
      if (error) throw error;
      
      toast?.success('Task created successfully!');
      setShowCreateModal(false);
      await loadTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      toast?.error(error?.message || 'Failed to create task');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const { error } = await agentService?.updateTask(taskId, updates);
      
      if (error) throw error;
      
      toast?.success('Task updated successfully!');
      await loadTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast?.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const { error } = await agentService?.deleteTask(taskId);
      
      if (error) throw error;
      
      toast?.success('Task deleted successfully!');
      await loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast?.error('Failed to delete task');
    }
  };

  const renderActiveView = () => {
    switch (activeView) {
      case 'chat':
        return (
          <div className="h-[600px]">
            <AgentChatInterface
              userId={user?.id}
              agentId="personal-productivity"
              agentName="Personal Productivity Agent"
              agentIcon="CheckSquare"
              agentDescription="Task management and organization expert"
            />
          </div>
        );
      case 'dashboard':
        return (
          <TaskDashboard
            tasks={tasks}
            onUpdateTask={handleUpdateTask}
            onDeleteTask={handleDeleteTask}
            onCreateTask={() => setShowCreateModal(true)}
          />
        );
      case 'calendar':
        return (
          <DeadlineCalendar
            tasks={tasks}
            onSelectDate={setSelectedDate}
            onUpdateTask={handleUpdateTask}
            onCreateTask={() => setShowCreateModal(true)}
          />
        );
      case 'digest':
        return <DailyDigestView tasks={tasks} />;
      case 'reminders':
        return <SMSReminderSettings userId={user?.id} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <NavigationSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Icon name="Loader2" size={48} className="animate-spin" color="var(--color-primary)" />
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="flex h-screen bg-neutral-50">
        <Toaster position="top-right" />
        <NavigationSidebar />
        
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center p-1.5">
                  <img 
                    src="/assets/images/Photo_Feb_21_2026_12_00_04_AM_1_-1771799585955.png" 
                    alt="DraftIQ Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-semibold text-foreground font-heading">
                    Productivity Agent
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    Task management with intelligent prioritization and automated reminders
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
                  <Icon name="Plus" size={18} />
                  <span className="hidden md:inline">New Task</span>
                </Button>
                <TutorialAccessButton onTutorialStart={() => {}} />
              </div>
            </div>
          </header>

          {/* View Tabs */}
          <div className="bg-card border-b border-border px-4 md:px-6 lg:px-8 flex-shrink-0">
            <div className="flex gap-1 overflow-x-auto">
              {views?.map((view) => (
                <button
                  key={view?.id}
                  onClick={() => setActiveView(view?.id)}
                  className={`
                    flex items-center gap-2 px-4 py-3 border-b-2 transition-all duration-250 whitespace-nowrap
                    ${activeView === view?.id
                      ? 'border-primary text-primary font-medium' :'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                    }
                  `}
                >
                  <Icon name={view?.icon} size={18} />
                  <span className="text-sm">{view?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto px-4 md:px-6 lg:px-8 py-6">
            {renderActiveView()}

            {/* Smart Scheduling Panel */}
            <div className="lg:col-span-2">
              <SmartSchedulingPanel onScheduleComplete={loadTasks} />
            </div>

            {/* Priority Matrix */}
            <div className="lg:col-span-2">
              <PriorityMatrix tasks={tasks} onTaskClick={(task) => setSelectedTask(task)} />
            </div>
          </main>
        </div>

        {/* Create Task Modal */}
        {showCreateModal && (
          <CreateTaskModal
            onClose={() => setShowCreateModal(false)}
            onCreate={handleCreateTask}
            preselectedDate={selectedDate}
          />
        )}
      </div>
    </ErrorBoundary>
  );
};

export default PersonalProductivityAgentInterface;